import { cn } from '@/lib/utils';
import { Loading } from '@/libs/components/src/lib/Loading';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Condition } from '@chiroup/core/types/Condition.type';
import { Muscle } from '@chiroup/core/types/Muscle.type';
import React, { useCallback, useEffect, useMemo } from 'react';
import { MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT } from '../../../../../constants';
import useMuscles from '../../../../../hooks/useMuscles';
import MuscleAutoCompleteMulti from './MuscleAutoCompleteMulti';
import MyofascialItem from './MyofascialItem';

// Helper to ensure region arrays contain unique entries
const dedupeRegions = (regions: string[] = []): string[] => Array.from(new Set<string>(regions));

type Props = {
  /** Shows an informational pop-up instead of the add-muscles autocomplete
   *  (trial SOAP sandbox). */
  onAddMusclesIntercept?: () => void;
  close?: () => void;
  value?: {
    [muscleId: string]: {
      value?: any;
      left?: boolean;
      right?: boolean;
      touched?: boolean;
      addedManually?: boolean;
      complaintIds?: string[];
      [key: string]: any;
    };
  };
  onChange: (detail: any) => void;
  conditions: {
    [key: number]: Partial<Condition>;
  };
  signed?: boolean;
};

const UnifiedMyofascial: React.FC<Props> = ({
  close,
  value,
  onChange,
  conditions,
  signed,
  onAddMusclesIntercept,
}) => {
  const { data: musclesData, isFetching: isFetchingMuscles } = useMuscles({
    refetchOnMount: false,
  });

  // Get regions from form state, with fallback to empty array
  const regions = useMemo(() => {
    return (value as any)?.__regions || [];
  }, [value]);

  const regionsManuallyModified = useMemo(() => {
    return (value as any)?.__regionsManuallyModified || false;
  }, [value]);

  const muscles = useMemo(() => {
    // Get existing muscles from value
    const existingMuscles = Object.keys(value || {})
      .filter(key => !key.startsWith('__'))
      .map(Number)
      .filter(num => !isNaN(num));

    // Always show existing muscles - they may have been auto-populated from conditions
    // Even if conditions prop is empty, we should display muscles that are in the value
    return existingMuscles;
  }, [value]);

  // Auto-populate regions from conditions only if not manually modified
  useEffect(() => {
    if (!regionsManuallyModified && Object.keys(conditions)?.length) {
      const newRegions = Object.values(conditions).reduce((acc: string[], condition) => {
        if (condition.regions?.length) {
          acc.push(...condition.regions);
        }

        return acc;
      }, []);

      // De-duplicate region codes to avoid duplicate keys in Combobox rendering
      const uniqueRegions = dedupeRegions(newRegions);

      // Only update if regions actually changed
      if (JSON.stringify(uniqueRegions) !== JSON.stringify(regions)) {
        onChange({
          ...value,
          __regions: uniqueRegions,
          __regionsManuallyModified: false,
        });
      }
    }
  }, [conditions, regionsManuallyModified, regions, value, onChange]);

  // Function to update regions and mark as manually modified
  const setRegions = useCallback(
    (newRegions: string[] | ((prev: string[]) => string[])) => {
      const regionsToSet =
        typeof newRegions === 'function' ? (newRegions(regions) as string[]) : newRegions;
      // Ensure unique region codes
      const uniqueRegions = dedupeRegions(regionsToSet);
      onChange({
        ...value,
        __regions: uniqueRegions,
        __regionsManuallyModified: true,
      });
    },
    [value, onChange, regions]
  );

  const handleItemChange = (val: {
    muscle: number;
    status?: string;
    e?: boolean;
    left?: boolean;
    right?: boolean;
  }) => {
    const currentValue = value || {};
    const muscleValue: any = { ...currentValue[val.muscle] };

    if (val.status) {
      muscleValue[val.status] = val.e;
    }

    muscleValue.left = val.left || false;
    muscleValue.right = val.right || false;
    muscleValue.touched = true;

    const newValue = {
      ...currentValue,
      [val.muscle]: muscleValue,
    };

    onChange(newValue);
  };

  const onChangeMuscles = (newMuscles: number[]) => {
    const newValue = { ...value };
    const existingMuscles = Object.keys(newValue).map(Number);
    const newMusclesAdded = newMuscles.filter(muscle => !existingMuscles.includes(muscle));

    existingMuscles.forEach(key => {
      if (!newMuscles.includes(key)) {
        delete newValue[key];
      }
    });

    newMusclesAdded.forEach(key => {
      newValue[key] = {
        addedManually: true, // Mark as manually added
      };
    });

    onChange(newValue);
  };

  const removeItem = (muscle: number) => {
    const newValue = { ...value };
    delete newValue[muscle];
    onChange(newValue);
  };

  const acceptSuggestions = () => {
    const newValue: any = { ...value };
    Object.keys(newValue).forEach(key => {
      if (!MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT[key]) {
        newValue[key] = {
          ...newValue[key],
          tender: true,
          hpn: true,
          tp: true,
          touched: true,
        };
      }
    });
    onChange(newValue);
  };

  const dontShowAcceptSuggestionsButton = useMemo(() => {
    const val = value || {};

    return Object.keys(val).some(key => {
      const muscle = val[key];

      return (
        muscle.tender ||
        muscle.hpn ||
        muscle.tp ||
        muscle.spasm ||
        muscle.weakness ||
        muscle.left ||
        muscle.right
      );
    });
  }, [value]);

  return (
    <div className="flex flex-col">
      {!isFetchingMuscles ? (
        <>
          {!!muscles?.length && (
            <Button
              onClick={acceptSuggestions}
              text="Accept suggestions"
              color={ButtonColors.plainWithBorder}
              className={cn(
                'm-2',
                !dontShowAcceptSuggestionsButton ? 'ring-2 ring-offset-2 ring-primary-600' : ''
              )}
              disabled={dontShowAcceptSuggestionsButton}
            />
          )}
          {!!muscles?.length && (
            <div className="overflow-hidden dark:bg-dark-gray-900">
              <ul className="divide-y divide-gray-300 dark:divide-dark-gray-700 rounded">
                {muscles
                  ?.sort((a, b) => {
                    const muscleA = musclesData?.data.find(m => m.ID === a) as Muscle;
                    const muscleB = musclesData?.data.find(m => m.ID === b) as Muscle;

                    return muscleA?.name?.localeCompare(muscleB?.name);
                  })
                  ?.map((muscle, i) => (
                    <MyofascialItem
                      muscle={musclesData?.data.find(m => m.ID === muscle)}
                      key={muscle}
                      onChange={handleItemChange}
                      value={value?.[muscle] || {}}
                      remove={removeItem.bind(null, muscle)}
                      firstItem={i === 0}
                      showRingAroundInput={
                        !dontShowAcceptSuggestionsButton &&
                        !MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT[muscle]
                      }
                      disabled={signed}
                    />
                  ))}
              </ul>
            </div>
          )}
          <div
            className="p-6 border-t border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 rounded-b-lg"
            // Trial SOAP sandbox: swallow the interaction before the
            // autocomplete focuses, so its dropdown never wedges the flow.
            onMouseDownCapture={e => {
              if (!onAddMusclesIntercept) return;
              e.preventDefault();
              e.stopPropagation();
            }}
            onClickCapture={e => {
              if (!onAddMusclesIntercept) return;
              e.preventDefault();
              e.stopPropagation();
              onAddMusclesIntercept();
            }}
          >
            <MuscleAutoCompleteMulti
              muscles={musclesData?.data || []}
              isFetchingMuscles={isFetchingMuscles}
              onChangeMuscles={onChangeMuscles}
              value={muscles}
              regions={regions}
              setRegions={setRegions}
              disabled={signed}
              hideSelected
              whiteBackground
            />
          </div>
        </>
      ) : (
        <Loading color="text-gray-400 mx-auto m-2" size={10} />
      )}
      {!!close && (
        <div className="flex flex-row justify-end">
          <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />
        </div>
      )}
    </div>
  );
};

export default UnifiedMyofascial;
